import React from 'react';
import styled from 'styled-components';
import { theme } from 'design';
import { CalculatorSplitMethod, IBeneficiary, ICalculatorRuleSharePolicyShare, IRemainderBeneficiary, ITipCalculatorResultWarningCappedShareDetailRule, ShareEligibilityMethod, ShareRuleType } from 'types';
import { getTipSources, toPercent, toTitleCase } from 'utils';

export const Semibold = styled.span`
  font-weight: ${theme.font.weight.semiBold};
`;

interface IPartitionedBeneficiaries {
  timeWorked: string[];
  evenly: string[];
  none: string[];
}

type IShareRule = ICalculatorRuleSharePolicyShare | ITipCalculatorResultWarningCappedShareDetailRule;

/**
 * Groups beneficiaries by their split method, returning title-cased names bucketed into time-worked, evenly, and none.
 * @param beneficiaries - The beneficiaries to partition.
 * @returns The beneficiary names grouped by split method.
 */
const partitionBeneficiaries = (beneficiaries: IBeneficiary[]): IPartitionedBeneficiaries =>
  beneficiaries.reduce<IPartitionedBeneficiaries>(
    (partitions, beneficiary) => {
      switch (beneficiary.split) {
        case CalculatorSplitMethod.TIME_WORKED:
          partitions.timeWorked.push(toTitleCase(beneficiary.name));
          break;
        case CalculatorSplitMethod.NONE:
          partitions.none.push(toTitleCase(beneficiary.name));
          break;
        default:
          partitions.evenly.push(toTitleCase(beneficiary.name));
          break;
      }
      return partitions;
    },
    { timeWorked: [], evenly: [], none: [] }
  );

/**
 * Renders the "X% of <source> per check/segment to <beneficiaries>" description for a tip-share rule.
 * @param rule - The share rule to describe.
 * @returns The rule description nodes.
 */
export const renderShareRuleDescription = (rule: IShareRule): React.ReactNode => {
  const { timeWorked, evenly, none } = partitionBeneficiaries(rule.beneficiaries);
  return (
    <>
      <Semibold>{toPercent(rule.share)}</Semibold>
      <span> of </span>
      <Semibold>{rule.type === ShareRuleType.TIP ? getTipSources(rule.sources) : toTitleCase(rule.category)}</Semibold>
      <span> per {rule.eligibilityMethod === ShareEligibilityMethod.BY_TICKET ? 'check' : 'segment'} to </span>
      {timeWorked.length > 0 && (
        <span>
          <Semibold>{timeWorked.join(' & ')}</Semibold> split by time worked{evenly.length > 0 ? ', ' : ''}
        </span>
      )}
      {evenly.length > 0 && (
        <span>
          <Semibold>{evenly.join(' & ')}</Semibold> split evenly
        </span>
      )}
      {none.length > 0 && <Semibold>{none.join(' & ')}</Semibold>}
    </>
  );
};

/**
 * Renders the "<participants> earning [N hours of] <source> split by ..." description for a remainder pool beneficiary.
 * @param beneficiary - The remainder beneficiary to describe.
 * @returns The beneficiary description nodes.
 */
export const renderRemainderBeneficiaryDescription = (beneficiary: IRemainderBeneficiary): React.ReactNode => {
  const hours = beneficiary.conditions?.[0]?.hours;
  return (
    <>
      <Semibold>{beneficiary.participants.map((participant) => toTitleCase(participant.name)).join(' & ')}</Semibold>
      <span> earning </span>
      <Semibold>
        {hours ? `${hours} hour${hours !== 1 ? 's' : ''} of ` : ''}
        {getTipSources(beneficiary.sources)}
      </Semibold>
      <span> split {beneficiary.split === CalculatorSplitMethod.TIME_WORKED ? 'by time worked' : 'evenly'}</span>
    </>
  );
};
